// Report content: one modal, two destinations.
//
//   kind: 'generation' — a tile the visitor just rendered on their own Sogni
//         account. Only the platform can act on that, so it goes to the same
//         feedback API sogni-web's report modal posts to, naming the project
//         and job so the report is actionable.
//   kind: 'stream'     — a tile published to the Infinity wall, which this app
//         hosts. It goes to our own flag route, where enough independent
//         reports withhold the tile pending moderator review.
//
// The reason list itself is served by /api/config so the wording and the
// values the routes validate cannot drift apart.

import { submitContentReport } from './sogni.js';
import { flagStreamEntry } from './stream.js';

const modal = document.getElementById('report-modal');
const titleEl = document.getElementById('report-title');
const subtitleEl = document.getElementById('report-subtitle');
const reasonsEl = document.getElementById('report-reasons');
const noteEl = document.getElementById('report-note');
const includeRow = document.getElementById('report-include-row');
const includeEl = document.getElementById('report-include');
const submitBtn = document.getElementById('report-submit');
const closeBtn = document.getElementById('report-close');
const statusEl = document.getElementById('report-status');

let reasons = [];
let target = null;
// The "thank you" dismisses itself. Held so opening a second report cancels
// it: without that, a report sent and dismissed inside the window closed the
// NEXT tile's form under whoever was typing in it.
let closeTimer = null;

export function setReportConfig(config) {
  reasons = Array.isArray(config?.reportReasons) ? config.reportReasons : [];
}

export function isReportModalOpen() {
  return !modal.classList.contains('hidden');
}

export function closeReportModal() {
  clearTimeout(closeTimer);
  closeTimer = null;
  modal.classList.add('hidden');
}

function paintReasons() {
  reasonsEl.innerHTML = '';
  reasons.forEach((reason, i) => {
    const id = `report-reason-${reason.value}`;
    const row = document.createElement('label');
    row.className = 'report-reason';
    row.innerHTML =
      `<input type="radio" name="report-reason" id="${id}" value="${reason.value}"${
        i === 0 ? ' checked' : ''
      } />` + `<span></span>`;
    // Label text is set as text, never markup: it is config-supplied.
    row.querySelector('span').textContent = reason.label;
    reasonsEl.appendChild(row);
  });
}

function chosenReason() {
  return reasonsEl.querySelector('input[name="report-reason"]:checked')?.value || null;
}

/**
 * Open the report modal for a tile.
 *
 * @param {object} item     what is being reported
 * @param {'generation'|'stream'} item.kind
 * @param {string} [item.name]        subject name, shown so the visitor can see
 *                                    which tile they are about to report
 * @param {string} [item.id]          stream entry id (stream reports)
 * @param {string} [item.projectId]   generation ids (generation reports)
 * @param {string} [item.jobId]
 * @param {string} [item.url]
 * @param {string} [item.prompt]
 * @param {string} [item.model]
 * @param {number} [item.seed]
 */
export function openReportModal(item) {
  if (!item?.kind) return;
  clearTimeout(closeTimer);
  closeTimer = null;
  target = item;
  if (!reasonsEl.children.length) paintReasons();
  titleEl.textContent = 'Report this tile';
  subtitleEl.textContent = item.name
    ? item.kind === 'stream'
      ? `“${item.name}” on the public stream`
      : `“${item.name}” from your render`
    : '';
  // Attaching the prompt and image is only a question for a platform report;
  // a stream tile is already public and the wall already stores both.
  includeRow.classList.toggle('hidden', item.kind !== 'generation');
  includeEl.checked = true;
  noteEl.value = '';
  statusEl.textContent = '';
  statusEl.classList.remove('is-error');
  submitBtn.disabled = false;
  submitBtn.textContent = 'Send report';
  const first = reasonsEl.querySelector('input[name="report-reason"]');
  if (first) first.checked = true;
  modal.classList.remove('hidden');
  // Focus the note rather than a radio: the reason has a sensible default and
  // the note is the field only the reporter can fill in.
  noteEl.focus();
}

submitBtn.onclick = async () => {
  if (!target) return;
  const reason = chosenReason();
  if (!reason) {
    statusEl.textContent = 'Pick a reason first.';
    statusEl.classList.add('is-error');
    return;
  }
  submitBtn.disabled = true;
  submitBtn.textContent = 'Sending…';
  statusEl.classList.remove('is-error');
  statusEl.textContent = '';
  try {
    if (target.kind === 'stream') {
      await flagStreamEntry(target.id, { reason, note: noteEl.value });
    } else {
      await submitContentReport({
        projectId: target.projectId,
        jobId: target.jobId,
        resultUrl: target.url,
        reason,
        note: noteEl.value,
        includeGeneration: includeEl.checked,
        prompt: target.prompt,
        model: target.model,
        seed: target.seed
      });
    }
    statusEl.textContent = 'Thank you — this has been sent for review.';
    submitBtn.textContent = 'Sent';
    target.onSent?.();
    closeTimer = setTimeout(closeReportModal, 1400);
  } catch (err) {
    statusEl.textContent = `Could not send that report — ${String(err.message || err).slice(0, 140)}`;
    statusEl.classList.add('is-error');
    submitBtn.disabled = false;
    submitBtn.textContent = 'Send report';
  }
};

closeBtn.onclick = closeReportModal;
modal.onclick = (e) => {
  if (e.target === modal) closeReportModal();
};

// Every other modal here closes on Escape. The fullscreen preview forwards it
// while it owns the keyboard; this covers a report opened from a thumbnail
// menu, where nothing else was listening and Cancel was the only way out.
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape' || !isReportModalOpen()) return;
  e.stopPropagation();
  closeReportModal();
});
